/**
 * Dialog components for Category management page.
 *
 * Contains Add/Edit Category and Add/Edit Dashboard dialogs.
 */

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import type { AnalyseCategory, AnalyseDashboard } from '../../lib/api/barratt';

// =========================================================================
// Edit Category Dialog
// =========================================================================

interface EditCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCategory: AnalyseCategory | null;
  onEditingCategoryChange: (cat: AnalyseCategory) => void;
  onSave: () => void;
}

export function EditCategoryDialog({
  open,
  onOpenChange,
  editingCategory,
  onEditingCategoryChange,
  onSave,
}: EditCategoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
          <DialogDescription>Update category details</DialogDescription>
        </DialogHeader>
        {editingCategory && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Category Name *</label>
              <Input
                placeholder="Enter category name"
                value={editingCategory.name}
                onChange={(e) =>
                  onEditingCategoryChange({ ...editingCategory, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Enter description (optional)"
                value={editingCategory.hint || ''}
                onChange={(e) =>
                  onEditingCategoryChange({ ...editingCategory, hint: e.target.value })
                }
                rows={3}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!editingCategory?.name.trim()}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Dashboard Form Fields (shared between Add and Edit)
// =========================================================================

interface DashboardFormData {
  name: string;
  description: string;
  is_enabled: boolean;
  shared_with: 'private' | 'company' | 'company_and_suppliers';
}

interface DashboardFormFieldsProps {
  data: DashboardFormData;
  onChange: (data: DashboardFormData) => void;
}

function DashboardFormFields({ data, onChange }: DashboardFormFieldsProps) {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Dashboard Name *</label>
        <Input
          placeholder="Enter dashboard name"
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Description</label>
        <Textarea
          placeholder="Enter description (optional)"
          value={data.description}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Sharing</label>
        <Select
          value={data.shared_with}
          onValueChange={(value: 'private' | 'company' | 'company_and_suppliers') =>
            onChange({ ...data, shared_with: value })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select sharing level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="private">Private - Only you</SelectItem>
            <SelectItem value="company">Company - All company users</SelectItem>
            <SelectItem value="company_and_suppliers">
              Shared - Company and suppliers
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Enabled</label>
        <Switch
          checked={data.is_enabled}
          onCheckedChange={(checked) =>
            onChange({ ...data, is_enabled: checked })
          }
        />
      </div>
    </div>
  );
}

// =========================================================================
// Add Dashboard Dialog
// =========================================================================

interface AddDashboardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboardData: DashboardFormData;
  onDashboardDataChange: (data: DashboardFormData) => void;
  onSave: () => void;
}

export function AddDashboardDialog({
  open,
  onOpenChange,
  dashboardData,
  onDashboardDataChange,
  onSave,
}: AddDashboardDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Dashboard</DialogTitle>
          <DialogDescription>Create a new dashboard in this category</DialogDescription>
        </DialogHeader>
        <DashboardFormFields data={dashboardData} onChange={onDashboardDataChange} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!dashboardData.name.trim()}>
            Add Dashboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Edit Dashboard Dialog
// =========================================================================

interface EditDashboardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingDashboard: AnalyseDashboard | null;
  onEditingDashboardChange: (dashboard: AnalyseDashboard) => void;
  onSave: () => void;
}

export function EditDashboardDialog({
  open,
  onOpenChange,
  editingDashboard,
  onEditingDashboardChange,
  onSave,
}: EditDashboardDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Dashboard</DialogTitle>
          <DialogDescription>Update dashboard details</DialogDescription>
        </DialogHeader>
        {editingDashboard && (
          <DashboardFormFields
            data={{
              name: editingDashboard.name,
              description: editingDashboard.description || '',
              is_enabled: editingDashboard.is_enabled,
              shared_with: editingDashboard.shared_with,
            }}
            onChange={(data) =>
              onEditingDashboardChange({
                ...editingDashboard,
                name: data.name,
                description: data.description,
                is_enabled: data.is_enabled,
                shared_with: data.shared_with,
              })
            }
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!editingDashboard?.name.trim()}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
